import { Injectable, OnDestroy, inject } from '@angular/core';
import { AuthResponse, AuthSession, SupabaseClient } from '@supabase/supabase-js';
import { BehaviorSubject } from 'rxjs';
import { SUPABASE_CLIENT } from '@app/providers/supabase.provider';

/**
 * 登入狀態以 Supabase 自己保存的 session 為唯一來源。
 *
 * 每台裝置登入時，Supabase 都會建立獨立的 session 與 refresh token，
 * 同帳號跨裝置登入、登出本來就互不影響，不需要額外複製或回填 token。
 * 舊版另外保存的共享 session 會在啟動時把舊 refresh token 寫回 Supabase，
 * 造成 token 被判定重複使用而整個 session 遭撤銷，因此已移除。
 */
@Injectable({
  providedIn: 'root'
})
export class MultiDeviceSessionService implements OnDestroy {
  // 舊版自行保存的 token 副本、模擬同步資料與裝置資訊，啟動時清除
  private static readonly LEGACY_STORAGE_KEYS = [
    'timeBank_shared_session',
    'timeBank_device_sessions',
    'timeBank_sync_operations',
    'timeBank_offline_operations',
    'timeBank_device_id',
    'timeBank_device_info'
  ];

  private supabaseClient: SupabaseClient;
  private unsubscribeAuthListener: (() => void) | null = null;
  private _currentSession$ = new BehaviorSubject<AuthSession | null>(null);

  public currentSession$ = this._currentSession$.asObservable();

  constructor() {
    this.supabaseClient = inject(SUPABASE_CLIENT);
    this.clearLegacyStorage();

    const { data: { subscription } } = this.supabaseClient.auth.onAuthStateChange((_event, session) => {
      this._currentSession$.next(session);
    });
    this.unsubscribeAuthListener = () => subscription.unsubscribe();
  }

  /**
   * 以帳號密碼登入，每次都建立這台裝置自己的 session。
   * 保留舊名稱以免呼叫端跟著改名。
   */
  async signInWithSessionSharing(phone: string, password: string): Promise<AuthResponse & { isNewDevice: boolean }> {
    const response = await this.supabaseClient.auth.signInWithPassword({ phone, password });
    return { ...response, isNewDevice: !!response.data?.session };
  }

  /**
   * 讀取目前 session；access token 將過期時 Supabase 會在鎖內自動換新。
   */
  async getCurrentSession(): Promise<AuthSession | null> {
    const { data } = await this.supabaseClient.auth.getSession();
    this._currentSession$.next(data.session);
    return data.session;
  }

  /**
   * 只登出這台裝置，其他裝置的 session 不受影響。
   */
  async signOutCurrentDevice(): Promise<void> {
    const { error } = await this.supabaseClient.auth.signOut({ scope: 'local' });
    if (error) {
      throw error;
    }
  }

  /**
   * 登出同帳號的所有裝置。
   */
  async signOutAllDevices(): Promise<void> {
    const { error } = await this.supabaseClient.auth.signOut({ scope: 'global' });
    if (error) {
      throw error;
    }
  }

  ngOnDestroy() {
    this.unsubscribeAuthListener?.();
    this.unsubscribeAuthListener = null;
  }

  private clearLegacyStorage() {
    if (typeof localStorage === 'undefined') {
      return;
    }

    for (const key of MultiDeviceSessionService.LEGACY_STORAGE_KEYS) {
      try {
        localStorage.removeItem(key);
      } catch {
        // 無法存取 localStorage（例如隱私模式）時略過
      }
    }
  }
}
